import {
  EnvironmentInjector,
  Injectable,
  Signal,
  computed,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  AccountRef,
  BreadcrumbAccountData,
  BreadcrumbData,
  BreadcrumbParent,
  BreadcrumbParentData,
} from './nav.models';
import { VISIBLE_ACCOUNTS } from './navigation.tokens';
import { routeChain, urlOf } from './route-snapshot';

export interface Breadcrumb {
  readonly label: string;
  /** Indirizzo del livello; `null` per la pagina corrente. */
  readonly link: string | null;
  readonly queryParams: Record<string, string> | null;
  readonly kind: 'page' | 'account';
}

interface PendingCrumb {
  readonly label: Signal<string>;
  readonly link: string;
  readonly account: Signal<AccountRef | null> | null;
  readonly parent: Signal<BreadcrumbParent | null> | null;
}

/**
 * Il percorso della testata (FR-025): ogni rotta lo dichiara con `data.breadcrumb`. Il servizio
 * aggiunge il livello dell'account solo se l'utente vede più di un account, e quello della
 * commessa solo se la rotta lo dichiara (`data.breadcrumbParent`) e la commessa è esplicita e
 * visibile all'utente (FR-053, SC-010).
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly injector = inject(EnvironmentInjector);
  private readonly accounts = inject(VISIBLE_ACCOUNTS);
  private readonly pending = signal<readonly PendingCrumb[]>([]);

  readonly crumbs = computed<Breadcrumb[]>(() => {
    const showAccount = this.accounts().length > 1;
    const result: Breadcrumb[] = [];
    for (const crumb of this.pending()) {
      const account = crumb.account?.() ?? null;
      if (showAccount && account) {
        result.push({
          label: account.name,
          link: '/',
          queryParams: { account: account.id },
          kind: 'account',
        });
      }
      const parent = crumb.parent?.() ?? null;
      if (parent) {
        result.push({ label: parent.label, link: parent.link, queryParams: null, kind: 'page' });
      }
      result.push({ label: crumb.label(), link: crumb.link, queryParams: null, kind: 'page' });
    }
    // L'ultimo livello è la pagina corrente: non è un collegamento.
    const last = result.at(-1);
    if (last) {
      result[result.length - 1] = { ...last, link: null, queryParams: null };
    }
    return result;
  });

  constructor() {
    this.update();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.update());
  }

  private update(): void {
    const crumbs: PendingCrumb[] = [];
    for (const route of routeChain(this.router.routerState.snapshot.root)) {
      // Solo i dati dichiarati dalla rotta stessa, non quelli ereditati dal genitore.
      const data = route.routeConfig?.data;
      const declared = data?.['breadcrumb'] as BreadcrumbData | undefined;
      if (declared === undefined) {
        continue;
      }
      const label =
        typeof declared === 'string'
          ? signal(declared).asReadonly()
          : runInInjectionContext(this.injector, () => declared(route));
      const accountData = data?.['breadcrumbAccount'] as BreadcrumbAccountData | undefined;
      const account = accountData
        ? runInInjectionContext(this.injector, () => accountData(route))
        : null;
      const parentData = data?.['breadcrumbParent'] as BreadcrumbParentData | undefined;
      const parent = parentData
        ? runInInjectionContext(this.injector, () => parentData(route))
        : null;
      crumbs.push({ label, link: urlOf(route), account, parent });
    }
    this.pending.set(crumbs);
  }
}
